import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, Observable, concatMap, map, of, takeUntil } from 'rxjs';
import { ODataService, ODataType, QueryOperator } from '@ui-kit/odata';
import * as moment from "moment";

@Component({
  selector: 'cont-patient-appointment',
  templateUrl: './create.html'
})
export class ContPatientAppoinmentCreateComponent implements OnInit, OnDestroy {

  private readonly unsubscribe$ = new Subject<void>()

  public medAppQueue: any
  public patientDetails: any
  public editPatientDetails: any
  public minDate: Date = moment().add(1, 'days').startOf("day").toDate()
  public maxDate: Date = moment().add(14, 'days').startOf("day").toDate()
  public medAppsQueuesAreLoading = false
  public medAppsQueues2$:  Observable<any> | undefined
  public patientDetails$:  Observable<any> | undefined
  public reservedAlert: any = null
  public medApp: any


  constructor(private odata: ODataService) {}


  ngOnInit() { this.patientDetails$ = this.getPatientDetailsByMasterId(sessionStorage.getItem('patient.id') as string); }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }


  public setMedicalAppointment(medicalAppointment: any) {
    this.medApp = medicalAppointment
    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues()
  }

  public setQueue2(event: any) {
    event.date = (typeof event.date === 'string' || event.date instanceof String) 
      ? event.date // floating feature -> sometimes date is not converted
      : moment(event.date).format('DD.MM.yyyy');

    event = {...this.medApp, ...event}

    this.odata.post(`patientAppointments/reserve?id=${event.id}`, { params: { }, body: {} })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({ 
        next: (o: any) => {
          this.medAppQueue = event;
        }, 
        error: (e) => {
          this.reservedAlert = { message: 'Обраний вами час запису на прийом недоступний.<br />Будь ласка оберіть інший час.' };
          this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
        } 
      })

  }

  public appointmentAdded(patientId: string) {
    this.medAppQueue = undefined;
    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
    this.patientDetails$ = this.getPatientDetails(patientId);
  }

  public appointmentCanceled() {
    this.medAppQueue = undefined;
    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
  }

  public appointmentDeleted(patientId: string) {
    this.medAppQueue = undefined;
    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
    this.patientDetails$ = this.getPatientDetails(patientId);
  }

  public applyProfile() {
    this.editPatientDetails = this.patientDetails
      ? Object.assign({}, this.patientDetails)
      : { id: '', lastName: '', firstName: '', patronymic: '', phone1: '' };
  }

  public profileSaved(patientId: string) {
    this.editPatientDetails = null;

    if (patientId) {
      this.patientDetails$ = this.getPatientDetails(patientId);
    }
  }



  private getPatientDetails(patientId: string): Observable<any> {

    if (!patientId) {
      return of(null);
    }

    return this.odata.get('patients', { 
      params: { 
        key: { type: ODataType.guid, value: patientId },
        select: [ 'id', 'lastName', 'firstName', 'patronymic', 'phone1', 'concurrencyStamp' ]
      },
    })
    .pipe(
      concatMap((patient: any) => { 
          return !patient
            ? of(null)
            : this.getPatientApps(patientId).pipe(map((apps: any[]) => {
                patient.appointments = apps;
                this.patientDetails = patient;
                return patient;
              }))
    }))
  }

  private getPatientDetailsByMasterId(masterId: string): Observable<any> {
    if (!masterId) {
      return of(null);
    }

    return this.odata.get('patients', { 
      params: { 
        filter: { masterId: { eq: masterId } }, 
        select: [ 'id' ]
      },
    })
    .pipe(
      concatMap((p: any[]) => {
        return p.length == 1
          ? this.getPatientDetails(p[0].id)
          : of(null)
      })
    )
  }

  private getPatientApps(patientId: string): Observable<any[]>  {
    if (!patientId) {
      return of([]);
    }

    return this.odata.get('patientAppointments', { 
        params: {
          filter: { 
            patientId: { type: ODataType.guid, value: patientId }, 
            'queue/date': { ge: { type: ODataType.raw, value: moment().format('yyyy-MM-DD') } }, 
          },
          expand: { 
            queue: { 
              select: ['date', 'timeBegin', 'timeEnd'],
              expand: { medicalAppointment: { select: ['serviceProviderTitle', 'specialtyTitle']} } }, },
          select: [ 'id', 'comment' ],
          orderBy: [ 'queue/date asc', 'queue/timeBegin asc' ]
        }
      }).pipe((map((o: any[]) => {

        o.forEach((oo: any) => {
          oo.queue.combinedTime = `${moment.utc(moment.duration(oo.queue.timeBegin).asMilliseconds()).format("HH:mm")}-` + 
            `${moment.utc(moment.duration(oo.queue.timeEnd).asMilliseconds()).format("HH:mm")}`;
          oo.queue.medicalAppointment.serviceProvider = `${oo.queue.medicalAppointment.serviceProviderTitle}, ${oo.queue.medicalAppointment.specialtyTitle}`;
        });

        return o;
      })))
  }

  private getAvailableMedAppsRangeQueues() : Observable<Date[]> {

    if (!(this.medApp && this.medApp.id)) {
      return of([]);
    }

    this.medAppsQueuesAreLoading = true;

    return this.odata.get('medicalAppointmentQueues', { 
      loadAllRecursive: true,
      params: {
        filter: {
          and: [
            {'medicalAppointment/scheduleId': { type: ODataType.guid, value: this.medApp.scheduleId }},
            { date: { 
              ge: { type: ODataType.raw, value: moment(this.minDate).format('yyyy-MM-DD') }, 
              le: { type: ODataType.raw, value: moment(this.maxDate).format('yyyy-MM-DD') } }},
            {
              or: [
                { reservedUntil: { eq: null } },
                { reservedUntil: { le: { type: ODataType.raw, value: new Date().toISOString() } } }
              ]
            }
          ]
        },
        expand: { patientAppointment: { select: ['id'] } },
        select: [ 'id', 'date', 'medicalAppointmentId', 'timeBegin', 'timeEnd' ],
        orderBy: [ 'date asc', 'timeBegin asc' ]
      }
    }).pipe(map((docAppQueues: any[]) => {

        docAppQueues = docAppQueues.filter(oo => !oo.patientAppointment); // remove engaged appointments (time-slots)
        docAppQueues = this.removeNextDayAppointments(docAppQueues); // check and remove out-of-range date-slots

        let result: any[] = []; // group flattened time-slots to date-slots-with-time-slots
        
        docAppQueues.forEach((maq: any) => {

          let remapped = {
            date: moment(maq.date, "yyyy-MM-DD").toDate(), // convert serialized-date to date
            id: maq.id,
            timeBegin: moment.utc(moment.duration(maq.timeBegin).asMilliseconds()).format("HH:mm"),
            combinedTime: `${moment.utc(moment.duration(maq.timeBegin).asMilliseconds()).format("HH:mm")} - ` + 
              `${moment.utc(moment.duration(maq.timeEnd).asMilliseconds()).format("HH:mm")}`,
          }

          let existingDateData = result.find((rx: any) => rx.strDate == maq.date);

          if (existingDateData) { // date-slot exists
            existingDateData.apps.push(remapped); // put time-slot to appointments of date-slot
          } else {                // date-slot not found
            result.push({ strDate: maq.date, date: remapped.date, apps: [ remapped ] }) // put new date-slot with it's time-slot
          }

        })

        this.medAppsQueuesAreLoading = false;

        return result;
      })
    )
  }

  private removeNextDayAppointments(allList: any[]): any[] {
    if (allList.length == 0) {
      return allList;
    }

    const marginalTime = '14:50';     // will be passed from db-configuration-specific-object later
    const dtNow = moment().toDate();
    const dtMarginal = moment(`${moment().format('DD.MM.yyyy')} ${marginalTime}`, 'DD.MM.YYYY HH:mm').toDate()
    const dtToday = moment().startOf('day').toDate();
    const isDayOff = dtToday.getDay() == 6 || dtToday.getDay() == 0; // is Saturday or Sunday

    if (!(dtNow >= dtMarginal || isDayOff)) {
      return allList;
    }

    let nextAvailableDate = moment(dtToday).add(2, 'days').toDate(); // tomorrow slots are not available
    switch (dtToday.getDay()) {
      case 5: // Friday
        nextAvailableDate = moment(dtToday).add(4, 'days').toDate(); // since Tuesday slots are available
        break
      case 6: // Saturday
        nextAvailableDate = moment(dtToday).add(3, 'days').toDate(); // since Tuesday slots are available
        break
    }

    allList = allList.map((o: any) => {
      o.dd = moment(o.date, "yyyy-MM-DD").toDate()   // convert to date for filtering
      return o;
    })

    return allList.filter((o: any) => o.dd >= nextAvailableDate) // remain only slots since nextAvailableDate
 }



  // Keep untill new implementation is approved
  // Old: calendar + quuesList components
  /* 

  public medAppsQueues$:  Observable<any> | undefined;
  public currDate = moment().add(1, 'days').startOf("day").toDate();//new Date();
  public disabledDates: Date[] = [ this.maxDate ];

  public setMedicalAppointment(medicalAppointment: any) {
    this.medApp = medicalAppointment;

    this.getAvailableMedAppsQueuesDates()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((disabledDates: any[]) => {
        this.disabledDates = disabledDates;

        this.medAppsQueues$ = this.getMedAppsQueues();
      });

    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
  }
  public setQueue(event: any) {
    event = {...this.medApp, ...event}
    this.medAppQueue = event;
    console.log('setQueue: this.medAppQueue: ', this.medAppQueue)
  }
  public appointmentAdded(patientId: string) {
    this.medAppQueue = undefined;
    this.medAppsQueues$ = this.getMedAppsQueues();
    this.medAppsQueues2$ = this.getAvailableMedAppsRangeQueues();
    this.patientDetails$ = this.getPatientDetails(patientId);
  }
  public setCurrDate(date: Date){
    this.currDate = date;
    this.medAppsQueues$ = this.getMedAppsQueues();
  }



  private getMedAppsQueues() : Observable<any> {

    if (!(this.medApp && this.medApp.id)) {
      return of([]);
    }

    this.medAppsQueuesAreLoading = true;

    return this.odata.get('medicalAppointmentQueues', { 
          params: {
            filter: {
              'medicalAppointment/scheduleId': { type: ODataType.guid, value: this.medApp.scheduleId },
              date: { [QueryOperator.eq]: { type: ODataType.raw, value: moment(this.currDate).format('yyyy-MM-DD') } },
            },
            expand: { patientAppointment: { select: ['id'] } },
            select: ['id', 'date', 'timeBegin', 'timeEnd', 'medicalAppointmentId' ],
            orderBy: [ 'timeBegin asc' ]
          }
        }
      ).pipe(map((o: any[]) => {
        this.medAppsQueuesAreLoading = false;
        console.log('oooooooooo: ', o)
        return o.filter(oo => !oo.patientAppointment);
      })
    )
  }
  private getAvailableMedAppsQueuesDates() : Observable<Date[]> {

    if (!(this.medApp && this.medApp.id)) {
      return of([]);
    }

    return this.odata.get('medicalAppointmentQueues', { 
          params: {
            func: {
              getAvailableDates: { 
                scheduleId: { type: ODataType.guid, value: this.medApp.scheduleId }, 
                dtBegin: { type: ODataType.raw, value: moment(this.minDate).format('yyyy-MM-DD') },
                dtEnd: { type: ODataType.raw, value: moment(this.maxDate).format('yyyy-MM-DD') },
              },
            }
          }
        }
      ).pipe(map((o: any[]) => {

        let disabledDates = [];

        let dtAppTimes = o.map((oo: string) => moment(oo).toDate().getTime());

        let currDate = this.minDate;
        while (currDate <= this.maxDate) { 
          if (dtAppTimes.indexOf(currDate.getTime()) < 0) {
            disabledDates.push(currDate);
          }
          currDate = moment(currDate).add(1, 'days').toDate();
        }

        return disabledDates;
      })
    )
  }
  private getAvailableMedAppsQueuesDates2() : Observable<Date[]> {

    if (!(this.medApp && this.medApp.id)) {
      return of([]);
    }

    // Current implementation leads to recursive load of redundant data - we are really need here only dates
    return this.odata.get('medicalAppointmentQueues', { 
          loadAllRecursive: true,
          params: {
            filter: {
              'medicalAppointment/scheduleId': { type: ODataType.guid, value: this.medApp.scheduleId },
              date: { 
                ge: { type: ODataType.raw, value: moment(this.minDate).format('yyyy-MM-DD') }, 
                le: { type: ODataType.raw, value: moment(this.maxDate).format('yyyy-MM-DD') } 
              }
            },
            select: ['date' ],
            orderBy: [ 'date asc' ]

            // This'll work on next version of oData - groupBy'll result as distinct (tested in test-project) - it fits to our needs
            // transform: [
            //   {
            //     filter: {
            //       'medicalAppointment/scheduleId': { type: ODataType.guid, value: this.medApp.scheduleId },
            //       date: { 
            //         ge: { type: ODataType.raw, value: moment(this.minDate).format('yyyy-MM-DD') }, 
            //         le: { type: ODataType.raw, value: moment(this.maxDate).format('yyyy-MM-DD') } 
            //       }
            //     }
            //   },
            //   { 
            //     groupBy: { 
            //       properties: ['date'], 
            //       // transform: {           // Transformation is redundant in current scenario
            //       //   aggregate: {
            //       //     date: {
            //       //       with: 'countdistinct',
            //       //       as: 'Total'
            //       //     }
            //       //   }
            //       // }
            //     } 
            //   }
            // ],
          }
        }
      ).pipe(map((o: any[]) => {

        console.log('111: o: ', o)

        let disabledDates = [];

        let strAppDates = o.map(oo => oo.date);
        let strAppDatesDistinct = Array.from(new Set(strAppDates));
        let dtAppTimes = strAppDatesDistinct.map((o: string) => moment(o, "yyyy-MM-DD").toDate().getTime());

        let currDate = this.minDate;
        while (currDate <= this.maxDate) { 
          if (dtAppTimes.indexOf(currDate.getTime()) < 0) {
            disabledDates.push(currDate);
          }
          currDate = moment(currDate).add(1, 'days').toDate();
        }

        return disabledDates;
      })
    )
  }


  */

}
